import React from "react";
import api from "../config/axiosConfig";

/**
 * useRfiStagesActivityLogs
 * Custom hook for fetching RFI stages activity logs for dimensional flow
 * API: /api/rfi-stages-activity-logs/rfi/:rfiId
 */
export default function useRfiStagesActivityLogs({
  rfiNo,
  open,
  limit = 20,
  sortBy = "action_timestamp",
  sortOrder = "DESC",
  actionTypes = null,
  stageNumbers = null,
  startDate = null,
  endDate = null,
  performedByUsers = null,
}) {
  const [page, setPage] = React.useState(1);
  const [isLoading, setIsLoading] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [totalPages, setTotalPages] = React.useState(1);
  const [totalLogs, setTotalLogs] = React.useState(0);

  const fetchActivityLogs = React.useCallback(async () => {
    if (!rfiNo) return;

    try {
      setIsLoading(true);
      
      // Build query parameters
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      // Add optional filters
      if (actionTypes && actionTypes.length > 0) {
        actionTypes.forEach((type) => params.append("action_type", type));
      }
      if (stageNumbers && stageNumbers.length > 0) {
        stageNumbers.forEach((stage) => params.append("stage_number", stage));
      }
      if (startDate) {
        params.append("start_date", startDate);
      }
      if (endDate) {
        params.append("end_date", endDate);
      }
      if (performedByUsers && performedByUsers.length > 0) {
        performedByUsers.forEach((userId) => params.append("performed_by", userId));
      }

      const res = await api.get(
        `/api/rfi-stages-activity-logs/rfi/${rfiNo}?${params.toString()}`
      );
      
      const data = res?.data?.data || {};
      
      // Extract logs array
      const items = Array.isArray(data.logs) ? data.logs : [];
      
      // Extract pagination info
      const pagination = data.pagination || {};
      const pages = Number(pagination.totalPages) || 1;
      const total = Number(pagination.total) || 0;

      setLogs(items);
      setTotalPages(pages);
      setTotalLogs(total);
    } catch (e) {
      console.error("Failed to fetch RFI stages activity logs", e);
      setLogs([]);
      setTotalPages(1);
      setTotalLogs(0);
    } finally {
      setIsLoading(false);
    }
  }, [rfiNo, page, limit, sortBy, sortOrder, actionTypes, stageNumbers, startDate, endDate, performedByUsers]);

  React.useEffect(() => {
    if (open) fetchActivityLogs();
  }, [open, fetchActivityLogs]);

  // Reset pagination when opening the dialog or changing filters
  React.useEffect(() => {
    if (open) setPage(1);
  }, [rfiNo, open, actionTypes, stageNumbers, startDate, endDate, performedByUsers]);

  return {
    page,
    setPage,
    isLoading,
    logs,
    totalPages,
    totalLogs,
    refetch: fetchActivityLogs,
  };
}
